// Starter library — first-run seeding for the public deployment.
//
// The public online copy of PageVoice has no personal /lib server behind
// it, so a fresh visitor would land on an empty shelf. Instead we ship eight
// public-domain EPUBs (Project Gutenberg + Standard Ebooks) in
// public/starter-library/ and
// import them through the normal pipeline on first launch.
//
// Guard rails — the owner's private tailnet copy must never be touched:
//   1. Only seeds when the LOCAL library is empty.
//   2. Only seeds when the CLOUD library is unavailable or empty. If the
//      /lib server answers with any books (or the cached manifest from a
//      previous launch has any), we bail — auto-sync will restore the
//      personal library instead.
//   3. Idempotent: a localStorage flag (same pattern as pv.cloudSync /
//      pv.cloudTombstones) records that seeding ran once, so deleting the
//      starter books doesn't resurrect them on the next load.
//   4. Non-blocking: every failure path returns quietly. A broken seed can
//      never break app startup.

import { db } from "./db";
import { PUBLIC_MODE } from "./remoteConfig";
import {
  checkCloudLibrary,
  fetchCloudManifest,
  readCachedCloudManifest,
} from "./cloudLibraryService";

const SEEDED_KEY = "pv.starterLibrarySeeded";
const MANIFEST_CACHE_KEY = "pv.cloudManifestCache";

/** One-time migration for the public deployment: earlier builds of the
 *  public site auto-synced a personal cloud library into visitors'
 *  IndexedDB. In PUBLIC_MODE, remove any local book that came from that
 *  cloud manifest (matched by content hash), clear the cloud caches, and
 *  un-mark seeding so the starter library takes over. No-op for anyone
 *  without a cached cloud manifest. Never throws. */
async function cleanupCloudRemnants(): Promise<void> {
  if (!PUBLIC_MODE) return;
  try {
    const raw = localStorage.getItem(MANIFEST_CACHE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    const shas = new Set(
      (Array.isArray(arr) ? arr : [])
        .map((b: { sha256?: string }) => b?.sha256)
        .filter(Boolean),
    );
    if (shas.size > 0) {
      const { deleteBook } = await import("./bookStorageService");
      const books = await db.books.toArray();
      let removed = 0;
      for (const b of books) {
        if (b.cloudSha256 && shas.has(b.cloudSha256)) {
          try {
            await deleteBook(b.id);
            removed++;
          } catch {
            /* keep going */
          }
        }
      }
      // Books were pulled from a cloud shelf that public mode no longer
      // uses — let the starter seeding decision run fresh.
      if (removed > 0) localStorage.removeItem(SEEDED_KEY);
    }
    localStorage.removeItem(MANIFEST_CACHE_KEY);
    localStorage.removeItem("pv.cloudTombstones");
  } catch {
    /* silent — cleanup must never break startup */
  }
}

interface StarterManifestEntry {
  title: string;
  author: string;
  file: string;
}

function alreadySeeded(): boolean {
  try {
    return localStorage.getItem(SEEDED_KEY) === "1";
  } catch {
    // localStorage blocked (private mode etc). Without a durable flag we
    // can't guarantee idempotence, so skip seeding entirely — an empty
    // shelf beats duplicate imports on every load.
    return true;
  }
}

function markSeeded(): void {
  try {
    localStorage.setItem(SEEDED_KEY, "1");
  } catch {
    /* no-op */
  }
}

// Per-file seed ledger (2026-07-11). The old boolean SEEDED_KEY froze
// the shelf at whatever the manifest contained on the visitor's FIRST
// load — when the starter set grew (foreign-language samples), returning
// devices never received the additions. The ledger records which starter
// files have been seeded on this device, so new manifest entries import
// on the next launch while files the visitor deleted stay deleted
// (guard-rail 3: deleting starter books must not resurrect them).
const SEEDED_FILES_KEY = "pv.starterSeededFiles";
// The starter set as it existed under the boolean flag era. Devices
// migrating from SEEDED_KEY="1" get this as their initial ledger.
const LEGACY_SEEDED_FILES = [
  "treasure-island.epub",
  "alice-in-wonderland.epub",
  "through-the-looking-glass.epub",
  "notes-from-underground.epub",
  "importance-of-being-earnest.epub",
  "adventures-of-sherlock-holmes.epub",
  "count-of-monte-cristo.epub",
  "candide.epub",
];

function readSeededFiles(): Set<string> | null {
  try {
    const raw = localStorage.getItem(SEEDED_FILES_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr.filter((f) => typeof f === "string"));
    }
    // Migrate from the boolean era: flag set means the original eight
    // were seeded (or intentionally settled) on this device.
    if (localStorage.getItem(SEEDED_KEY) === "1") {
      return new Set(LEGACY_SEEDED_FILES);
    }
    return new Set();
  } catch {
    return null; // localStorage blocked — caller must skip seeding
  }
}

function writeSeededFiles(files: Set<string>): void {
  try {
    localStorage.setItem(SEEDED_FILES_KEY, JSON.stringify([...files]));
  } catch {
    /* no-op */
  }
}

/** Resolve a starter-library asset relative to the deploy base, so it works
 *  at / in dev and under /pagevoice/ in production. */
function starterUrl(file: string): string {
  const base = (import.meta.env.BASE_URL as string) || "/";
  return `${base.endsWith("/") ? base : base + "/"}starter-library/${file}`;
}

/** The bundled starter set's exact file names. Used to (a) identify
 *  wrongly-seeded starter books on the owner build and (b) stop the
 *  LibraryView backfill from pushing seeded starter books into the
 *  owner's personal cloud shelf. Kept in sync with
 *  public/starter-library/manifest.json. */
export const STARTER_FILE_NAMES: ReadonlySet<string> = new Set([
  "treasure-island.epub",
  "alice-in-wonderland.epub",
  "through-the-looking-glass.epub",
  "notes-from-underground.epub",
  "importance-of-being-earnest.epub",
  "adventures-of-sherlock-holmes.epub",
  "count-of-monte-cristo.epub",
  "candide.epub",
  // Foreign-language originals — demonstrate the Translate feature.
  "die-verwandlung-de.epub",
  "also-sprach-zarathustra-de.epub",
  "candide-fr.epub",
  "don-quijote-es.epub",
  "pinocchio-it.epub",
  "botchan-ja.epub",
]);

/** Owner-build migration (2026-07-10): earlier builds could fire the
 *  starter seed on the tailnet/owner copy when /lib looked unreachable
 *  (relative /lib on the hub origin hit serve.py and 404'd). If that
 *  happened, remove the seeded starter books — identified by their
 *  exact starter fileName AND absence from the personal cloud manifest
 *  (so a genuinely-owned copy of the same classic is never touched).
 *  Requires the cloud manifest to be reachable; otherwise defers to the
 *  next launch. Clears the seeded flag afterwards so auto-sync owns the
 *  shelf again. Never throws. */
async function cleanupStarterRemnants(): Promise<void> {
  try {
    // Standing invariant (2026-07-10, per Thomas: "don't load the pub
    // books on local"): the owner build never keeps starter-set files
    // that aren't part of the personal cloud shelf — regardless of the
    // seeded flag, since polluted devices may have already cleared it.
    // Caveat: a hand-imported EPUB named exactly like a starter file
    // (e.g. "candide.epub") will be removed on relaunch unless it's on
    // the cloud shelf; rename the file to keep a personal copy.
    const health = await checkCloudLibrary();
    if (!health?.ok) return; // can't tell what's the owner's — retry later
    const manifest = await fetchCloudManifest().catch(() => null);
    if (manifest === null) return;
    const cloudShas = new Set(
      manifest.map((cb) => cb.sha256).filter(Boolean) as string[],
    );
    const { deleteBook } = await import("./bookStorageService");
    const books = await db.books.toArray();
    for (const b of books) {
      const isStarterFile = STARTER_FILE_NAMES.has(b.fileName);
      const onCloud = b.cloudSha256 ? cloudShas.has(b.cloudSha256) : false;
      if (isStarterFile && !onCloud) {
        try {
          await deleteBook(b.id);
        } catch {
          /* keep going */
        }
      }
    }
    localStorage.removeItem(SEEDED_KEY);
  } catch {
    /* silent — cleanup must never break startup */
  }
}

/** One-time public reset (2026-07-10): the droplet copy previously served
 *  the owner's personal library, so returning devices may still hold
 *  those books in IndexedDB — which also blocked the starter seed (the
 *  shelf wasn't empty). PUBLIC_MODE resets the local shelf once so every
 *  device converges on exactly the bundled starter set. */
const PUBLIC_RESET_KEY = "pv.publicResetV1";
async function publicResetOnce(): Promise<void> {
  try {
    if (localStorage.getItem(PUBLIC_RESET_KEY) === "1") return;
    const { deleteBook } = await import("./bookStorageService");
    const books = await db.books.toArray();
    for (const b of books) {
      try {
        await deleteBook(b.id);
      } catch {
        /* keep going */
      }
    }
    localStorage.removeItem(SEEDED_KEY);
    localStorage.removeItem(MANIFEST_CACHE_KEY);
    localStorage.removeItem("pv.cloudTombstones");
    localStorage.setItem(PUBLIC_RESET_KEY, "1");
  } catch {
    /* silent */
  }
}

/** Seed the starter library if this looks like a fresh public install.
 *  Seeding is PUBLIC_MODE-only (2026-07-10): the owner/tailnet build
 *  must never seed — its shelf belongs to the personal cloud library.
 *  Returns the number of books imported (0 when seeding was skipped).
 *  Never throws. */
export async function maybeSeedStarterLibrary(): Promise<number> {
  try {
    if (!PUBLIC_MODE) {
      await cleanupStarterRemnants();
      return 0;
    }
    await publicResetOnce();
    await cleanupCloudRemnants();
    const seededFiles = readSeededFiles();
    if (seededFiles === null) return 0; // localStorage blocked — no idempotence

    // If a previous launch saw a personal cloud library, this is the
    // owner's device even if the tailnet is unreachable right now.
    if (readCachedCloudManifest().length > 0) return 0;

    // If the cloud library is reachable AND has books, auto-sync owns this
    // shelf — hands off. (Reachable-but-empty falls through: a fresh public
    // deploy with a bundled coordinator still deserves the starter books.)
    const health = await checkCloudLibrary();
    if (health?.ok) {
      const cloud = await fetchCloudManifest().catch(() => null);
      // On manifest error, be conservative: don't seed, don't mark, retry
      // the whole decision on the next launch.
      if (cloud === null || cloud.length > 0) return 0;
    }

    // Fetch the starter manifest. Missing manifest (e.g. a deployment that
    // strips the folder) is a silent no-op.
    const res = await fetch(starterUrl("manifest.json"));
    if (!res.ok) return 0;
    const entries = (await res.json()) as StarterManifestEntry[];
    if (!Array.isArray(entries) || entries.length === 0) return 0;

    const localBooks = await db.books.toArray();
    const localFiles = new Set(localBooks.map((b) => b.fileName));

    // In-use-but-never-seeded device: a visitor who imported their own
    // books before ever seeing the starter set keeps a clean shelf —
    // settle the ledger so we never re-check. (Pre-ledger behavior,
    // preserved.)
    if (localBooks.length > 0 && seededFiles.size === 0) {
      writeSeededFiles(new Set(entries.map((e) => e.file).filter(Boolean)));
      markSeeded();
      return 0;
    }

    // Top-up set: manifest entries this device has never seeded AND
    // doesn't already have. Files the visitor deleted are in the ledger,
    // so they stay deleted.
    const toSeed = entries.filter(
      (e) => e?.file && !seededFiles.has(e.file) && !localFiles.has(e.file),
    );
    if (toSeed.length === 0) {
      markSeeded();
      writeSeededFiles(
        new Set([...seededFiles, ...entries.map((e) => e.file).filter(Boolean)]),
      );
      return 0;
    }

    // Import through the existing pipeline so covers/metadata generate
    // normally. Lazy import mirrors cloudLibraryService and avoids any
    // module-load-time cycle with bookStorageService.
    const { importEpub } = await import("./bookStorageService");

    let done = 0;
    for (const entry of toSeed) {
      try {
        const r = await fetch(starterUrl(entry.file));
        if (!r.ok) continue;
        const blob = await r.blob();
        const file = new File([blob], entry.file, {
          type: "application/epub+zip",
        });
        await importEpub(file);
        seededFiles.add(entry.file);
        done++;
      } catch {
        // Skip a bad download and keep going.
      }
    }

    // Persist the ledger after every pass; failed downloads stay out of
    // it so the next launch retries just those. Legacy flag kept in sync
    // for older code paths that still read it.
    if (done > 0) {
      writeSeededFiles(seededFiles);
      markSeeded();
    }
    return done;
  } catch {
    return 0;
  }
}
